import { useMemo } from 'react';

import { VALIDATION_REG_EXP } from '@endurancewarranty/lib';
import { useAccessToken } from '@hooks/auth';
import { DeleteRequest, GetRequest, PatchRequest, PostRequest, PutRequest } from '@lib/http';
import { impersonationAtom } from '@states/impersonation/impersonationAtom';
import { useJotaiValue } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';

import type { TBody, TPayloadValue } from '@lib/types/THttpRequest';

type HttpRequestFunctions = {
  // Opting to put these functions behind a namespace. I think
  // the context here matters and functions with short names
  // like 'get' and 'delete' can be misleading.
  http: {
    get: (endpoint?: string, query?: Maybe<Record<string, TPayloadValue>>) => GetRequest;
    post: (endpoint: string, body?: TBody, query?: Record<string, TPayloadValue>) => PostRequest;
    put: (endpoint: string, body?: Maybe<Record<string, TPayloadValue>>) => PutRequest;
    patch: (endpoint: string, body?: TBody) => PatchRequest;
    delete: (
      endpoint: string,
      query?: Maybe<Record<string, TPayloadValue>>,
      body?: Maybe<Record<string, TPayloadValue>>,
    ) => DeleteRequest;
  };
};

export function useHttp(): HttpRequestFunctions {
  const contact = useJotaiValue(contactSelector);
  const impersonation = useJotaiValue(impersonationAtom);
  const { getAccessToken, refreshAndGetAccessToken } = useAccessToken();

  const headers = useMemo(() => {
    const headersImpl: Record<string, string> = {};

    if (impersonation.azureAdId != null) {
      headersImpl['x-impersonation-azureadid'] = impersonation.azureAdId;
    }

    if (contact?.contactId != null && contact?.contactMasterId != null) {
      headersImpl['x-contact-id'] = contact.contactId;
      headersImpl['x-contact-master-id'] = contact.contactMasterId;

      if (
        contact?.contactPhoneNumber != null &&
        contact?.contactPhoneNumber.match(VALIDATION_REG_EXP.phoneNumber) &&
        contact?.isTransfer !== true // The contact phone # on a transfer is an Endurance # due to how InContact handles transfers
      ) {
        headersImpl['x-contact-phone-number'] = contact.contactPhoneNumber;
      }
    }

    return headersImpl;
  }, [
    contact.contactId,
    contact.contactMasterId,
    contact.contactPhoneNumber,
    contact.isTransfer,
    impersonation.azureAdId,
  ]);

  // TODO: @Kyle These request helpers dont provide much value anymore. Remove/replace
  return {
    http: {
      get: (endpoint?: string, query?: Maybe<Record<string, TPayloadValue>>) =>
        new GetRequest(endpoint, getAccessToken, query, headers, refreshAndGetAccessToken),
      post: (endpoint: string, body?: TBody, query?: Record<string, TPayloadValue>): PostRequest =>
        new PostRequest(endpoint, getAccessToken, body, query, headers, refreshAndGetAccessToken),
      put: (endpoint: string, body?: Maybe<Record<string, TPayloadValue>>): PutRequest =>
        new PutRequest(endpoint, getAccessToken, body, headers, refreshAndGetAccessToken),
      patch: (endpoint: string, body?: TBody): PatchRequest =>
        new PatchRequest(endpoint, getAccessToken, body, headers, refreshAndGetAccessToken),
      delete: (
        endpoint: string,
        query?: Maybe<Record<string, TPayloadValue>>,
        body?: Maybe<Record<string, TPayloadValue>>,
      ): DeleteRequest =>
        new DeleteRequest(endpoint, getAccessToken, query, body, headers, refreshAndGetAccessToken),
    },
  };
}
